import {
  ALERT_AUDIENCE,
  ALERT_EMAIL,
  ALERT_EMAIL_LABEL,
  ALERT_KINDS,
  ALERT_KIND_HELP,
  ALERT_KIND_LABEL,
  AUDIENCE_LABEL,
  AUTOMATION_DEFAULTS,
  AUTOMATION_HELP,
  AUTOMATION_LABEL,
  INTAKE_TIERS,
  SCORE_BANDS,
  SIGNAL_KINDS,
  TIER_LABEL,
  bandLabel,
  type Alert,
  type AlertAudience,
  type AlertDryRun,
  type AlertEmail,
  type AlertKind,
  type AlertParams,
  type AlertRule,
  type Automation,
  type AutomationDryRun,
  type AutomationParams,
  type IntakeTier,
} from '@cyberscore/contracts';
import { Bell, BellRing, CheckCheck, FlaskConical, Loader2, Pencil, Plus, Workflow } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { hashQuery, href, navigate, useHash } from '../../lib/router';
import { useSession, can } from '../../lib/session';
import { Drawer } from '../../ui/Overlay';
import { useToast } from '../../ui/Toasts';
import { when } from '../clients/parts';
import {
  automationDryRun,
  ruleDryRun,
  useAlertRuleChanges,
  useAlertRules,
  useAlerts,
  useAutomations,
  useMarkRead,
  useSaveAutomation,
  useUnderwriters,
} from './api';

const words = (s: string) => s.replace(/_/g, ' ');
/** The first field issue says what to fix; the bare message only that the request was refused. */
const message = (e: unknown, fallback: string) =>
  e instanceof ApiError ? (e.issues[0]?.message ?? e.message) : fallback;

/**
 * Alerts (task 70): what the organisation's alert rules caught for this person, the rules themselves, and
 * the automation templates that chase what is late and route new submissions.
 */
export function AlertsPage() {
  const hash = useHash();
  const { tenant } = useSession();
  const manages = can(tenant, 'alert_rules.create', 'alert_rules.edit');
  const readsRules = can(tenant, 'alert_rules.view');
  const asked = hashQuery(hash).get('tab');
  const tab = readsRules && (asked === 'rules' || asked === 'automations') ? asked : 'inbox';
  return (
    <>
      <div className="page-head">
        <h1>Alerts</h1>
        <p>What changed on your clients that needs a look: score drops, failed kill switches, overdue conditions, expiring documents and new outside-in findings.</p>
      </div>
      {readsRules && (
        <div className="seg list-tabs" role="group" aria-label="Show">
          <button type="button" aria-pressed={tab === 'inbox'} onClick={() => navigate(href.alerts())}><Bell size={14} /> For you</button>
          <button type="button" aria-pressed={tab === 'rules'} onClick={() => navigate(href.alerts('?tab=rules'))}><BellRing size={14} /> Rules</button>
          <button type="button" aria-pressed={tab === 'automations'} onClick={() => navigate(href.alerts('?tab=automations'))}><Workflow size={14} /> Automations</button>
        </div>
      )}
      {tab === 'inbox' ? <InboxView /> : tab === 'rules' ? <RulesView manages={manages} /> : <AutomationsView manages={manages} />}
    </>
  ); // prettier-ignore
}

// —— for you ——

function InboxView() {
  const alerts = useAlerts();
  const read = useMarkRead();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const items = (alerts.data?.items ?? []).filter((a) => !unreadOnly || !a.read);
  const open = async (a: Alert) => {
    if (!a.read) await read.mutateAsync([a.id]).catch(() => undefined);
    navigate(href.assessment(a.assessment_id));
  };
  return (
    <section className="card">
      <div className="dv-bar">
        <label className="toggle-row small"><input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} /> <span>Unread only</span></label>
        <span className="grow" />
        {!!alerts.data?.unread && (
          <button type="button" className="btn btn-sm" disabled={read.isPending} onClick={() => read.mutate(undefined)}><CheckCheck size={14} /> Mark all {alerts.data.unread} read</button>
        )}
      </div>
      {alerts.isPending ? <div className="empty">Loading…</div> : !items.length ? (
        <div className="empty"><Bell size={28} /><strong>{unreadOnly ? 'Nothing unread' : 'No alerts yet'}</strong>When one of your organisation’s alert rules catches something on a client you work on, it shows here.</div>
      ) : (
        <ul className="alert-list" aria-label="Alerts">
          {items.map((a) => (
            <li key={a.id} className={a.read ? '' : 'unread'}>
              <button type="button" className="alert-item" onClick={() => open(a)}>
                <span className={`pill ${KIND_TONE[a.kind]}`}>{ALERT_KIND_LABEL[a.kind]}</span>
                <span className="minw grow">
                  <b>{a.customer_name}</b> · {a.title}
                  {a.detail && <span className="muted small alert-detail">{a.detail}</span>}
                  <span className="muted small">{a.rule_name} · {when(a.at)}</span>
                </span>
                {!a.read && <span className="dot" aria-label="Unread" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  ); // prettier-ignore
}

const KIND_TONE: Record<AlertKind, string> = { score_drop: 'warn', kill_switch: 'bad', condition_overdue: 'warn', document_expiring: 'neutral', outside_in_finding: 'neutral', decision_waiting: 'warn' }; // prettier-ignore

// —— rules ——

function RulesView({ manages }: { manages: boolean }) {
  const rules = useAlertRules(true);
  const [editing, setEditing] = useState<AlertRule | 'new' | null>(null);
  return (
    <section className="card">
      <div className="dv-bar">
        <span className="muted small grow">Rules are checked as their last editor, on the clients they can see. Changes are caught as they happen; overdue conditions and expiring documents once a day.</span>
        {manages && <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}><Plus size={15} /> New rule</button>}
      </div>
      {rules.isPending ? <div className="empty">Loading…</div> : !rules.data?.length ? (
        <div className="empty"><BellRing size={28} /><strong>No alert rules yet</strong>Start with a score drop of 50 points, or kill switches that newly fail.</div>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Rule</th><th className="wide">Tells</th><th className="wide">How</th><th className="right">Last 30 days</th><th>State</th>{manages && <th />}</tr></thead>
            <tbody>
              {rules.data.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.name}</b><div className="muted small">{ALERT_KIND_LABEL[r.kind]}{paramText(r.kind, r.params)}</div></td>
                  <td className="wide small">{ALERT_AUDIENCE.filter((a) => r.audience.includes(a)).map((a) => AUDIENCE_LABEL[a]).join(', ')}</td>
                  <td className="wide small">{ALERT_EMAIL_LABEL[r.email]}</td>
                  <td className="right num">{r.recent}</td>
                  <td>{r.active ? <span className="pill ok">On</span> : <span className="pill neutral">Off</span>}</td>
                  {manages && <td className="right"><button type="button" className="btn btn-sm btn-ghost" aria-label={`Edit ${r.name}`} onClick={() => setEditing(r)}><Pencil size={14} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <RuleDrawer rule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </section>
  ); // prettier-ignore
}

function paramText(kind: AlertKind, p: AlertParams) {
  if (kind === 'score_drop')
    return `: ${[p.points !== undefined ? `${p.points}+ points` : '', p.band_below ? `below ${bandLabel(p.band_below)}` : ''].filter(Boolean).join(' or ')}`; // prettier-ignore
  if (kind === 'condition_overdue') return p.days ? `: ${p.days}+ days late` : '';
  if (kind === 'document_expiring') return `: within ${p.days ?? 30} days`;
  if (kind === 'decision_waiting') return `: waiting ${p.days ?? 2}+ days`;
  if (kind === 'outside_in_finding' && p.signal_kinds) return `: ${p.signal_kinds.map(words).join(', ')}`;
  return '';
}

function RuleDrawer({ rule, onClose }: { rule: AlertRule | null; onClose: () => void }) {
  const toast = useToast();
  const { create, update } = useAlertRuleChanges();
  const [name, setName] = useState(rule?.name ?? 'Score drops');
  const [kind, setKind] = useState<AlertKind>(rule?.kind ?? 'score_drop');
  const [points, setPoints] = useState(rule?.params.points?.toString() ?? '50');
  const [band, setBand] = useState(rule?.params.band_below ?? '');
  const [days, setDays] = useState(rule?.params.days?.toString() ?? '');
  const [kinds, setKinds] = useState<string[]>(rule?.params.signal_kinds ?? []);
  const [audience, setAudience] = useState<AlertAudience[]>(rule?.audience ?? ['underwriter']);
  const [mail, setMail] = useState<AlertEmail>(rule?.email ?? 'off');
  const [active, setActive] = useState(rule?.active ?? true);
  const [banner, setBanner] = useState<string | null>(null);
  const [trial, setTrial] = useState<AlertDryRun | null>(null);
  const [trying, setTrying] = useState(false);

  const params = (): AlertParams => {
    if (kind === 'score_drop') return { ...(points ? { points: Number(points) } : {}), ...(band ? { band_below: band as AlertParams['band_below'] } : {}) }; // prettier-ignore
    if (kind === 'condition_overdue' || kind === 'document_expiring' || kind === 'decision_waiting')
      return days ? { days: Number(days) } : {};
    if (kind === 'outside_in_finding') return kinds.length ? { signal_kinds: kinds as AlertParams['signal_kinds'] } : {}; // prettier-ignore
    return {};
  };
  const body = () => ({ name: name.trim(), kind, params: params(), audience, email: mail, active });

  const tryIt = async () => {
    setBanner(null);
    setTrying(true);
    try {
      setTrial(await ruleDryRun(body()));
    } catch (e) {
      setBanner(message(e, 'Could not try the rule'));
    } finally {
      setTrying(false);
    }
  };
  const save = async () => {
    setBanner(null);
    try {
      if (rule) await update.mutateAsync({ id: rule.id, revision: rule.revision, ...body() });
      else await create.mutateAsync(body());
      toast(rule ? 'Rule saved.' : 'Rule added.');
      onClose();
    } catch (e) {
      setBanner(message(e, 'Could not save the rule'));
    }
  };
  const busy = create.isPending || update.isPending;

  return (
    <Drawer
      title={rule ? 'Edit alert rule' : 'New alert rule'}
      subtitle="Who to tell, when something changes on a client."
      onClose={onClose}
      footer={<><button type="button" className="btn" disabled={trying || !audience.length} onClick={tryIt}>{trying ? <Loader2 size={15} className="spin" /> : <FlaskConical size={15} />} Try it</button><span className="grow" /><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" disabled={busy || !name.trim() || !audience.length} onClick={save}>{busy && <Loader2 size={15} className="spin" />}{rule ? 'Save rule' : 'Add rule'}</button></>} // prettier-ignore
    >
      {banner && <div className="result bad" role="alert"><span>{banner}</span></div>}
      <div className="field"><label htmlFor="ar-name">Name</label><input id="ar-name" className="input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="field"><label htmlFor="ar-kind">When</label>
        <select id="ar-kind" className="input" value={kind} onChange={(e) => { setKind(e.target.value as AlertKind); setTrial(null); }}>
          {ALERT_KINDS.map((k) => <option key={k} value={k}>{ALERT_KIND_LABEL[k]}</option>)}
        </select>
        <span className="hint">{ALERT_KIND_HELP[kind]}</span>
      </div>
      {kind === 'score_drop' && (
        <div className="two">
          <div className="field"><label htmlFor="ar-points">Falls by at least (points)</label><input id="ar-points" className="input" type="number" min={5} max={500} value={points} onChange={(e) => setPoints(e.target.value)} /></div>
          <div className="field"><label htmlFor="ar-band">Or falls below</label>
            <select id="ar-band" className="input" value={band} onChange={(e) => setBand(e.target.value)}>
              <option value="">No band line</option>
              {SCORE_BANDS.slice(1).map((b) => <option key={b.key} value={b.key}>{bandLabel(b.key)} ({b.min})</option>)}
            </select>
          </div>
        </div>
      )}
      {(kind === 'condition_overdue' || kind === 'document_expiring' || kind === 'decision_waiting') && (
        <div className="field"><label htmlFor="ar-days">{kind === 'condition_overdue' ? 'Overdue by at least (days)' : kind === 'decision_waiting' ? 'Waiting at least (days)' : 'Expiring within (days)'}</label><input id="ar-days" className="input" type="number" min={0} max={365} placeholder={kind === 'condition_overdue' ? '0' : kind === 'decision_waiting' ? '2' : '30'} value={days} onChange={(e) => setDays(e.target.value)} /></div>
      )}
      {kind === 'outside_in_finding' && (
        <fieldset className="field checks"><legend>Only these findings (all when none ticked)</legend>
          {SIGNAL_KINDS.map((k) => <label key={k} className="toggle-row small"><input type="checkbox" checked={kinds.includes(k)} onChange={(e) => setKinds(e.target.checked ? [...kinds, k] : kinds.filter((x) => x !== k))} /> <span>{words(k)}</span></label>)}
        </fieldset>
      )}
      <fieldset className="field checks"><legend>Tell</legend>
        {ALERT_AUDIENCE.map((a) => <label key={a} className="toggle-row small"><input type="checkbox" checked={audience.includes(a)} onChange={(e) => setAudience(e.target.checked ? [...audience, a] : audience.filter((x) => x !== a))} /> <span>{AUDIENCE_LABEL[a]}</span></label>)}
      </fieldset>
      <div className="field"><label htmlFor="ar-email">Also by email</label>
        <select id="ar-email" className="input" value={mail} onChange={(e) => setMail(e.target.value as AlertEmail)}>
          {ALERT_EMAIL.map((m) => <option key={m} value={m}>{ALERT_EMAIL_LABEL[m]}</option>)}
        </select>
      </div>
      <label className="toggle-row small"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> <span>On</span></label>
      {trial && <TrialResult trial={trial} />}
    </Drawer>
  ); // prettier-ignore
}

function TrialResult({ trial }: { trial: AlertDryRun }) {
  return (
    <div className="dry-run" aria-live="polite">
      <b>{trial.count === 0 ? 'Nothing to alert on' : `${trial.count} alert${trial.count === 1 ? '' : 's'}`}</b>{' '}
      <span className="muted small">{trial.window === 'today' ? 'if it ran today' : 'in the last 30 days'}; nothing was saved or sent.</span>
      {!!trial.items.length && (
        <ul className="plain-list small">
          {trial.items.map((i, n) => <li key={n}><b>{i.customer_name}</b> · {i.title} <span className="muted">{when(i.at)}</span></li>)}
        </ul>
      )}
    </div>
  ); // prettier-ignore
}

// —— automations ——

function AutomationsView({ manages }: { manages: boolean }) {
  const automations = useAutomations(true);
  if (automations.isPending)
    return (
      <section className="card">
        <div className="empty">Loading…</div>
      </section>
    );
  return (
    <div className="stack">
      {(automations.data ?? []).map((a) => <AutomationCard key={a.template} a={a} manages={manages} />)}
    </div>
  ); // prettier-ignore
}

function AutomationCard({ a, manages }: { a: Automation; manages: boolean }) {
  const toast = useToast();
  const save = useSaveAutomation();
  const people = useUnderwriters(manages && a.template === 'submission_routing');
  const initial = (a.params ?? { template: a.template, ...AUTOMATION_DEFAULTS[a.template] }) as Record<string, unknown>; // prettier-ignore
  const [vals, setVals] = useState<Record<string, unknown>>(initial);
  const [active, setActive] = useState(a.active);
  const [trial, setTrial] = useState<AutomationDryRun | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [trying, setTrying] = useState(false);
  const id = `au-${a.template}`;
  const params = { ...vals, template: a.template } as AutomationParams;
  const num = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setVals({ ...vals, [k]: e.target.value === '' ? '' : Number(e.target.value) }); // prettier-ignore

  const tryIt = async () => {
    setBanner(null);
    setTrying(true);
    try {
      setTrial(await automationDryRun(params));
    } catch (e) {
      setBanner(message(e, 'Could not try it'));
    } finally {
      setTrying(false);
    }
  };
  const store = async () => {
    setBanner(null);
    try {
      await save.mutateAsync({ template: a.template, params, active, revision: a.revision });
      toast(`${AUTOMATION_LABEL[a.template]}: ${active ? 'on' : 'off'}.`);
    } catch (e) {
      setBanner(message(e, 'Could not save'));
    }
  };

  return (
    <section className="card pad automation" aria-labelledby={`${id}-title`}>
      <div className="row-between">
        <h2 id={`${id}-title`} className="section-title">{AUTOMATION_LABEL[a.template]}</h2>
        {a.revision ? (a.active ? <span className="pill ok">On</span> : <span className="pill neutral">Off</span>) : <span className="pill neutral">Not set up</span>}
      </div>
      <p className="muted small">{AUTOMATION_HELP[a.template]}{a.revision ? ` Did ${a.recent} thing${a.recent === 1 ? '' : 's'} in the last 30 days.` : ''}{a.updated_by_name ? ` Saved by ${a.updated_by_name}, ${when(a.updated_at!)}.` : ''}</p>
      {banner && <div className="result bad" role="alert"><span>{banner}</span></div>}
      <fieldset className="automation-fields" disabled={!manages}>
        {a.template === 'renewal_chase' && (
          <div className="two">
            <div className="field"><label htmlFor={`${id}-after`}>First reminder after (days open)</label><input id={`${id}-after`} className="input" type="number" min={1} max={60} value={String(vals.after_days ?? '')} onChange={num('after_days')} /></div>
            <div className="field"><label htmlFor={`${id}-every`}>Then every (days)</label><input id={`${id}-every`} className="input" type="number" min={2} max={30} value={String(vals.every_days ?? '')} onChange={num('every_days')} /></div>
          </div>
        )}
        {a.template === 'condition_chase' && (
          <div className="two">
            <div className="field"><label htmlFor={`${id}-late`}>When overdue by (days)</label><input id={`${id}-late`} className="input" type="number" min={0} max={60} value={String(vals.overdue_days ?? '')} onChange={num('overdue_days')} /></div>
            <div className="field"><label htmlFor={`${id}-every`}>Then every (days)</label><input id={`${id}-every`} className="input" type="number" min={2} max={30} value={String(vals.every_days ?? '')} onChange={num('every_days')} /></div>
          </div>
        )}
        {a.template === 'submission_routing' && (
          <>
            <div className="field"><label htmlFor={`${id}-to`}>Send to</label>
              <select id={`${id}-to`} className="input" value={String(vals.to_user ?? '')} onChange={(e) => setVals({ ...vals, to_user: e.target.value })}>
                <option value="">Pick someone</option>
                {(people.data ?? []).map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
              </select>
            </div>
            <fieldset className="field checks"><legend>Submissions for</legend>
              {INTAKE_TIERS.map((t) => { const tiers = (vals.tiers as IntakeTier[] | undefined) ?? []; return <label key={t} className="toggle-row small"><input type="checkbox" checked={tiers.includes(t)} onChange={(e) => setVals({ ...vals, tiers: e.target.checked ? [...tiers, t] : tiers.filter((x) => x !== t) })} /> <span>{TIER_LABEL[t]}</span></label>; })}
            </fieldset>
          </>
        )}
        <label className="toggle-row small"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> <span>On (runs every hour)</span></label>
      </fieldset>
      {manages && (
        <div className="row gap">
          <button type="button" className="btn" disabled={trying} onClick={tryIt}>{trying ? <Loader2 size={15} className="spin" /> : <FlaskConical size={15} />} Try it</button>
          <button type="button" className="btn btn-primary" disabled={save.isPending} onClick={store}>{save.isPending && <Loader2 size={15} className="spin" />}Save</button>
        </div>
      )}
      {trial && (
        <div className="dry-run" aria-live="polite">
          <b>{trial.actions.length === 0 ? 'Nothing to do right now' : `Would do ${trial.actions.length} thing${trial.actions.length === 1 ? '' : 's'} now`}</b>{' '}
          <span className="muted small">Nothing was sent.</span>
          {!!trial.actions.length && <ul className="plain-list small">{trial.actions.slice(0, 50).map((x) => <li key={x.target}><b>{x.customer_name}</b> · {x.what} <span className="muted">→ {x.to.join(', ')}</span></li>)}</ul>}
        </div>
      )}
    </section>
  ); // prettier-ignore
}
